import {
	Button,
	CardActions,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import {
	AccountInfo,
	StaffInfoResDto,
} from '@/api/DTO/staff/staffList.interface';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import {
	useAddStaffMutation,
	useEditStaffMutation,
} from '@/api/staff/staffApiSlice';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { UserRole } from '@/api/enum/userRole.enum';

interface StallInfoFormProps {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
	staffInfo?: StaffInfoResDto;
	id?: string;
}

export default function StaffInfoForm({
	type,
	setType,
	staffInfo,
	id,
}: StallInfoFormProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [addStaff] = useAddStaffMutation();
	const [editStaff] = useEditStaffMutation();
	const [needConfirm, setNeedConfirm] = useState(false);
	const showModal = useModal();

	const staffList = [
		{ name: t('common.admin'), id: UserRole.ROLE_WEB_ADMIN },
		{ name: t('common.staff'), id: UserRole.ROLE_WEB_STAFF },
	];

	const defaultValues: DefaultValues<AccountInfo> = useMemo(
		() => ({
			roleId: staffInfo?.role || UserRole.ROLE_WEB_STAFF,
			name: staffInfo?.name || '',
			email: staffInfo?.email || '',
		}),
		[]
	);

	const formSchema = Yup.object().shape({
		name: Yup.string()
			.required(t('errorMessage.required'))
			.max(50, t('errorMessage.max50')),
		email: Yup.string()
			.required(t('errorMessage.required'))
			.email(t('errorMessage.email')),
	});
	const methods = useForm<AccountInfo>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<AccountInfo> = async (data) => {
		if (type === FormType.create) {
			const response = await addStaff(data);
			if ('data' in response) {
				enqueueSnackbar(t('snackbar.createStaffSuccess'), {
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
				navigate('/staff-manage/staff-list');
			}
		} else {
			data.id = id || '';
			const response = await editStaff(data);
			if ('data' in response) {
				enqueueSnackbar(t('snackbar.editStaffSuccess'), {
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type === FormType.edit) {
			setType(FormType.view);
		} else {
			navigate('/staff-manage/staff-list');
		}
	};
	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<AppDropDownMenu
									control={methods.control}
									name='roleId'
									label={t('staff.permission')}
									options={staffList}
									required
									disabled
								/>
							</Grid>
							<Grid item xs={12}>
								<AppTextField
									control={methods.control}
									name='email'
									label={t('staff.email')}
									disabled={type === FormType.edit}
								/>
							</Grid>
							<Grid item xs={12}>
								<AppTextField
									control={methods.control}
									name='name'
									label={t('staff.staffName')}
								/>
							</Grid>
							{type === FormType.create && (
								<Grid item xs={12} sx={{ display: 'flex' }}>
									<Typography
										sx={{
											display: 'flex',
											backgroundColor: '#EBEFFF',
											color: '#1D2F99',
											padding: '10px 12px',
											borderRadius: '8px',
										}}
									>
										<ErrorOutlineIcon />
										&nbsp;
										{t('staff.addStaffAccountMessage')}
									</Typography>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
